import { SymptomCardData } from 'features/patient/_components/symptom-card/types';
import { TestRecord } from 'lib/api/impact/types';
import {
  tScoreCategory,
  tScoreMapping,
  TScores
} from 'lib/documents/promisTScores';
import { z } from 'zod';
import { RequiredVisualisationData, SymptomCardDataGenerator } from './types';

/**
 * Curried getPromisTotalScore
 * @param subsections
 * @returns
 */
export function promisCurry(
  subsections: (keyof typeof tScoreMapping)[],
  category: tScoreCategory
): SymptomCardDataGenerator {
  const fn = (args: RequiredVisualisationData): SymptomCardData[] =>
    args.promis.map((value) => ({
      assessmentType: tryGetAtKey(args.assessmentIndexes, value.id)
        .assessmentType,
      date: value.date,
      value: subsections
        .map(subsection =>
          value.data[subsection] === null
            ? 0
            : TScores.findTScore(value.data[subsection], category, 0)
        )
        .reduce((acc, cur) => acc + cur, 0)
    }));

  fn.source = subsections.map(_ => `promis:${_}`);
  return fn;
}

/**
 * Helper method to get a specified score from a list of
 * ImPACT `TestRecord`'s transformed into `Inventory`'s
 */
export function impactCurry(
  query: keyof Pick<
    TestRecord,
    | 'userMemoryCompositeScoreVerbal'
    | 'userMemoryCompositeScoreVisual'
    | 'userImpulseControlCompositeScore'
    | 'userReactionTimeCompositeScore'
    | 'userVisualMotorCompositeScore'
  >
): SymptomCardDataGenerator {
  const fn = (args: RequiredVisualisationData): SymptomCardData[] =>
    args.impact.map((test) => {
      return {
        assessmentType: tryGetAtKey(args.assessmentIndexes, test.id)
          .assessmentType,
        date: test.date,
        value: z.number().parse(test.data[query])
      };
    });
  fn.source = [`impact:${query}`];
  return fn;
}

export function tryGetAtKey<Out extends { id: string }>(data: Out[], key: string): Out {
  const val = data.find(_ => _.id === key)
  if (!val) {
    throw new Error(`[tryGetAtKey] Value at key '${key}' was not found`)
  }
  return val;
}

export function tryGetAtIndex<T>(arr: T[], index: number) {
  const val = arr.at(index);
  if (!val) {
    throw new Error(`[tryGetAtIndex] Value at index ${index} not found`);
  }

  return val;
}
